import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";
import type { MentorMatch, MentorSession } from "./types";

/**
 * Mentor requests + sessions for the current user, viewed both as the
 * "member" (people I asked) and as the "mentor" (people who asked me).
 */
export const useMentorRequests = () => {
  const { user } = useAuth();
  const [asMember, setAsMember] = useState<MentorMatch[]>([]);
  const [asMentor, setAsMentor] = useState<MentorMatch[]>([]);
  const [sessions, setSessions] = useState<MentorSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) {
      setAsMember([]);
      setAsMentor([]);
      setSessions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    const [memRes, menRes, sessMemRes, sessMenRes] = await Promise.all([
      supabase
        .from("mentor_matches")
        .select("*")
        .eq("member_id", user.id)
        .order("created_at", { ascending: false }),
      supabase
        .from("mentor_matches")
        .select("*")
        .eq("mentor_user_id", user.id)
        .order("created_at", { ascending: false }),
      supabase
        .from("mentor_sessions")
        .select("*")
        .eq("member_id", user.id)
        .order("scheduled_at", { ascending: false }),
      supabase
        .from("mentor_sessions")
        .select("*")
        .eq("mentor_user_id", user.id)
        .order("scheduled_at", { ascending: false }),
    ]);

    if (memRes.error) setError(memRes.error.message);
    setAsMember((memRes.data as MentorMatch[]) ?? []);
    setAsMentor((menRes.data as MentorMatch[]) ?? []);

    const mergedSessions = [
      ...((sessMemRes.data as MentorSession[]) ?? []),
      ...((sessMenRes.data as MentorSession[]) ?? []),
    ];
    // Dedup by id
    const byId = new Map(mergedSessions.map((s) => [s.id, s]));
    setSessions(Array.from(byId.values()));
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  const requestMentor = useCallback(
    async (mentorUserId: string, app: string, message?: string) => {
      if (!user) return { error: "Not signed in" };
      const { data, error } = await supabase
        .from("mentor_matches")
        .insert({
          member_id: user.id,
          mentor_user_id: mentorUserId,
          app,
          status: "pending",
          message: message ?? null,
        } as any)
        .select("*")
        .maybeSingle();
      if (error) return { error: error.message };
      await load();
      return { data };
    },
    [user, load],
  );

  const respondToRequest = useCallback(
    async (matchId: string, status: "accepted" | "declined") => {
      const { error } = await supabase
        .from("mentor_matches")
        .update({ status })
        .eq("id", matchId);
      if (error) return { error: error.message };
      await load();
      return { ok: true };
    },
    [load],
  );

  return {
    asMember,
    asMentor,
    sessions,
    loading,
    error,
    reload: load,
    requestMentor,
    respondToRequest,
  };
};
